import axios from 'axios';
import { store } from '../redux-toolkit/redux_store/store';
import { 
  setSuperadminRefreshToken, 
  setSuperadminAccessTokenExpiry, 
  clearSuperadminRefreshToken 
} from '../redux-toolkit/reducer/superadminRefreshToken_reducer';

// Global variable to track the current timeout ID
let currentRefreshTimeoutId = null;

// Global variable to prevent multiple simultaneous refresh attempts
let isRefreshingToken = false;

// Global variable to track if we've had a permanent refresh failure
let hasRefreshFailed = false;

// Track the last time we attempted a refresh to handle sleep/wake scenarios
let lastRefreshAttempt = 0;

// Minimum time between refresh attempts (in milliseconds)
const MIN_REFRESH_INTERVAL = 30000; // 30 seconds

// Function to check if token is expired or about to expire
export const isSuperadminTokenExpired = (expiryTimestamp) => {
  if (!expiryTimestamp) {
    //console.log('[Superadmin Token Refresh] No expiry timestamp provided');
    return true;
  }
  
  // Check if token expires in the next 5 minutes (300 seconds)
  const currentTime = Math.floor(Date.now() / 1000);
  const bufferTime = 300; // 5 minutes buffer
  
  // Calculate time remaining
  const timeRemaining = expiryTimestamp - currentTime;
  const minutesRemaining = Math.floor(timeRemaining / 60);
  const secondsRemaining = timeRemaining % 60;
  
  // Also check if token has already expired
  const isAlreadyExpired = expiryTimestamp <= currentTime;
  const isExpiringSoon = (expiryTimestamp - bufferTime) <= currentTime;
  const isExpired = isAlreadyExpired || isExpiringSoon;
  
  return isExpired;
};

// Function to get new refresh token
export const getNewSuperadminRefreshToken = async () => {
  // Prevent multiple simultaneous refresh attempts
  if (isRefreshingToken) {
    console.log('[Superadmin Token Refresh] Refresh already in progress, skipping...');
    throw new Error('Token refresh already in progress');
  }

  const state = store.getState();
  const currentRefreshToken = state.superadminRefreshTokenReducer.refreshToken;
  
  try {
    if (!currentRefreshToken) {
      throw new Error('No refresh token available');
    }

    isRefreshingToken = true;
    console.log('[Superadmin Token Refresh] Starting token refresh...');

    const accessToken = localStorage.getItem('superadmin_token');
    
    // Create a timeout promise that rejects after 30 seconds
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Token refresh timeout - API call took too long'));
      }, 30000); // 30 seconds timeout
    });

    // Race the API call against the timeout
    const response = await Promise.race([
      axios.post(
        `${process.env.REACT_APP_SUPERADMIN_BASEURL}/getNewToken`,
        {
          refreshToken: currentRefreshToken
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': accessToken
          }
        }
      ),
      timeoutPromise
    ]);

    if (response.data.status === true && response.data.token) {
      // Update the access token in localStorage
      localStorage.setItem('superadmin_token', response.data.token);
      
      // Update the access token expiry in Redux
      store.dispatch(setSuperadminAccessTokenExpiry({
        accessToken: response.data.token
      }));

      store.dispatch(setSuperadminRefreshToken({
        refreshToken: response?.data?.refreshToken
      }));
      
      // Reset the failure flag on successful refresh
      hasRefreshFailed = false;
      
      console.log('[Superadmin Token Refresh] Token refresh successful');
      return response.data;
    } else {
      // Any failure response should trigger logout
      throw new Error('Failed to refresh token');
    }
  } catch (error) {
    if (error.message.includes('timeout')) {
      console.log('[Superadmin Token Refresh] Token refresh timed out - API call took too long');
    } else {
      console.log('[Superadmin Token Refresh] Token refresh failed:', error.message);
    }
    // Don't automatically logout - let the retry mechanism handle temporary failures
    // Only logout for permanent failures (403, Invalid Token) which are handled elsewhere
    throw error;
  } finally {
    isRefreshingToken = false;
  }
};

// Function to handle superadmin logout
export const handleSuperadminLogoutAndRedirect = () => {
  // Clear any scheduled refresh
  clearScheduledRefresh();
  
  // Clear refresh flags
  isRefreshingToken = false;
  hasRefreshFailed = false;
  
  // Clear tokens from Redux (this also clears from localStorage)
  store.dispatch(clearSuperadminRefreshToken());
  
  // Clear other tokens from localStorage
  localStorage.removeItem('superadmin_token');
  localStorage.removeItem('superadmin_refresh_token');
  localStorage.removeItem('__csadmin__d');
  
  // Redirect to login page
  window.location.href = '/admin/superadmin/login';
};

// Function to check and refresh token if needed
export const checkAndRefreshSuperadminToken = async () => {
  const state = store.getState();
  const { refreshToken, accessTokenExpiry } = state.superadminRefreshTokenReducer;
  
  // Don't attempt to refresh if no refresh token exists
  if (!refreshToken) {
    return;
  }

  // Only refresh tokens if we're on a superadmin page
  const isOnSuperadminPage = window.location.pathname.includes('/superadmin');
  
  if (!isOnSuperadminPage) {
    console.log('[Superadmin Token Refresh] Skipping refresh - not on superadmin page');
    return;
  }

  const currentTime = Date.now();
  
  // Check if we've had a recent failure and not enough time has passed
  if (hasRefreshFailed && (currentTime - lastRefreshAttempt) < MIN_REFRESH_INTERVAL) {
    console.log('[Superadmin Token Refresh] Skipping refresh attempt due to recent failure, will retry in', Math.ceil((MIN_REFRESH_INTERVAL - (currentTime - lastRefreshAttempt)) / 1000), 'seconds');
    return;
  }

  // Reset failure flag if enough time has passed (allows recovery from temporary issues)
  if (hasRefreshFailed && (currentTime - lastRefreshAttempt) >= MIN_REFRESH_INTERVAL) {
    console.log('[Superadmin Token Refresh] Resetting failure flag after cooldown period, attempting refresh...');
    hasRefreshFailed = false;
  }
  
  // Check if access token is expired or about to expire
  if (isSuperadminTokenExpired(accessTokenExpiry)) {    
    try {
      lastRefreshAttempt = currentTime;
      await getNewSuperadminRefreshToken();
      // Reset failure flag on successful refresh
      hasRefreshFailed = false;
    } catch (error) {
      // Mark as failed but allow retry after cooldown period
      hasRefreshFailed = true;
      lastRefreshAttempt = currentTime;
      
      // Determine if this is a permanent failure (invalid token) or temporary (network/timeout)
      const isPermanentFailure = error.message.includes('Invalid Token') || 
                                error.message.includes('No refresh token available') ||
                                error.response?.status === 403 || // 403 Forbidden is permanent
                                error.message.includes('Request failed with status code 403');
      
      if (isPermanentFailure) {
        console.error('[Superadmin Token Refresh] Permanent token failure detected (403/Invalid Token), stopping refresh system:', error.message);
        // For 403 errors after logout, completely stop the token refresh system
        if (error.response?.status === 403 || error.message.includes('Request failed with status code 403')) {
          handleSuperadminLogoutAndRedirect();
        }
      } else {
        console.error('[Superadmin Token Refresh] Temporary token refresh failure, will retry after cooldown:', error.message);
      }
      
      throw error; // Re-throw so scheduler knows to stop
    }
  }
};

// Helper function to clear existing refresh schedule
const clearScheduledRefresh = () => {
  if (currentRefreshTimeoutId) {
    clearTimeout(currentRefreshTimeoutId);
    currentRefreshTimeoutId = null;
  }
};

// Helper function to schedule token refresh based on expiry
const scheduleSuperadminTokenRefresh = () => {
  // Clear any existing scheduled refresh first
  clearScheduledRefresh();
  
  // Only schedule refresh if we're on a superadmin page
  const isOnSuperadminPage = window.location.pathname.includes('/superadmin');
  
  if (!isOnSuperadminPage) {
    console.log('[Superadmin Token Refresh] Not scheduling refresh - not on superadmin page');
    return null;
  }
  
  const state = store.getState();
  const { accessTokenExpiry } = state.superadminRefreshTokenReducer;
  
  if (!accessTokenExpiry) {
    return null;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  const bufferTime = 300; // 5 minutes buffer
  const refreshTime = accessTokenExpiry - bufferTime;
  const timeUntilRefresh = Math.max(0, (refreshTime - currentTime) * 1000);
  
  // Calculate when refresh will happen
  const refreshInMinutes = Math.floor(timeUntilRefresh / 60000);
  const refreshInSeconds = Math.floor((timeUntilRefresh % 60000) / 1000);
  
  // If token needs immediate refresh (already in buffer zone or expired)
  if (timeUntilRefresh === 0) {
    checkAndRefreshSuperadminToken().then(() => {
      // After successful refresh, schedule next check based on new token expiry
      scheduleSuperadminTokenRefresh();
    }).catch((error) => {
      console.log('[Superadmin Token Refresh] Immediate refresh failed, will retry after cooldown period:', error.message);
      // Schedule a retry after the cooldown period for temporary failures
      const isPermanentError = error.message.includes('Invalid Token') ||
                              error.response?.status === 403 ||
                              error.message.includes('Request failed with status code 403');
      if (!isPermanentError) {
        setTimeout(() => {
          scheduleSuperadminTokenRefresh();
        }, MIN_REFRESH_INTERVAL);
      } else {
        console.log('[Superadmin Token Refresh] Stopping scheduler due to permanent failure (403/Invalid Token)');
      }
    });
    return null;
  }
  
  console.log(`[Superadmin Token Refresh] Next refresh scheduled in ${refreshInMinutes}m ${refreshInSeconds}s`);
  
  // Schedule the refresh and store the timeout ID
  currentRefreshTimeoutId = setTimeout(() => {
    // Double-check we're still on superadmin page before executing
    const stillOnSuperadminPage = window.location.pathname.includes('/superadmin');
    
    if (!stillOnSuperadminPage) {
      console.log('[Superadmin Token Refresh] Cancelling scheduled refresh - no longer on superadmin page');
      return;
    }
    
    checkAndRefreshSuperadminToken().then(() => {
      // After successful refresh, schedule next check based on new token expiry
      scheduleSuperadminTokenRefresh();
    }).catch((error) => {
      console.log('[Superadmin Token Refresh] Scheduled refresh failed, will retry after cooldown period:', error.message);
      // Schedule a retry after the cooldown period for temporary failures
      const isPermanentError = error.message.includes('Invalid Token') ||
                              error.response?.status === 403 ||
                              error.message.includes('Request failed with status code 403');
      if (!isPermanentError) {
        setTimeout(() => {
          scheduleSuperadminTokenRefresh();
        }, MIN_REFRESH_INTERVAL);
      } else {
        console.log('[Superadmin Token Refresh] Stopping scheduler due to permanent failure (403/Invalid Token)');
      }
    });
  }, timeUntilRefresh);
  
  return currentRefreshTimeoutId;
};

// Export clear function for external use
export const clearSuperadminRefreshSchedule = clearScheduledRefresh;

// Function to reset refresh failure flag (call this on successful login)
export const resetSuperadminRefreshFailure = () => {
  hasRefreshFailed = false;
  lastRefreshAttempt = 0;
  console.log('[Superadmin Token Refresh] Refresh failure flag and attempt tracking reset');
};

// Track when the page was last visible to detect sleep/wake
let lastVisibilityChange = Date.now();

// Handle page visibility changes (detects sleep/wake scenarios)
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    const now = Date.now();
    const timeDiff = now - lastVisibilityChange;
    
    // Only handle visibility changes if we're on a superadmin page
    const isOnSuperadminPage = window.location.pathname.includes('/superadmin');
    
    // If more than 2 minutes have passed since last visibility change,
    // assume the system was asleep and check tokens immediately
    if (timeDiff > 120000 && isOnSuperadminPage) { // 2 minutes
      console.log('[Superadmin Token Refresh] System wake detected on superadmin page, checking token status...');
      
      // Reset the failure flag to allow refresh attempts after wake
      if (hasRefreshFailed) {
        console.log('[Superadmin Token Refresh] Resetting failure flag after system wake');
        hasRefreshFailed = false;
      }
      
      // Check and refresh token if needed
      checkAndRefreshSuperadminToken().then(() => {
        // Reschedule refresh based on current token status
        scheduleSuperadminTokenRefresh();
      }).catch((error) => {
        console.log('[Superadmin Token Refresh] Token check after wake failed:', error.message);
      });
    }
  }
  lastVisibilityChange = Date.now();
};

// Setup token refresh based on access token expiry
export const setupSuperadminTokenRefreshInterval = () => {  
  // Clear any existing schedule first
  clearScheduledRefresh();
  
  // Only add visibility change listener if we're on a superadmin page
  const isOnSuperadminPage = window.location.pathname.includes('/superadmin');
  
  if (typeof document !== 'undefined' && isOnSuperadminPage) {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    console.log('[Superadmin Token Refresh] Added visibility change listener for superadmin pages');
  }
  
  // Schedule refresh based on token expiry
  const timeoutId = scheduleSuperadminTokenRefresh();
  
  return timeoutId;
};

// Function to cleanup event listeners
export const cleanupSuperadminTokenRefresh = () => {
  clearScheduledRefresh();
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    console.log('[Superadmin Token Refresh] Removed visibility change listener');
  }
};

// Function to stop superadmin token refresh when navigating away from superadmin pages
export const stopSuperadminTokenRefresh = () => {
  console.log('[Superadmin Token Refresh] Stopping superadmin token refresh system');
  clearScheduledRefresh();
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  }
};